<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { UseStoreUser } from '@/store/userStore';

const undrawLink = require('@/assets/images/undraw_link.svg');

const { t } = useI18n();
const userStore = UseStoreUser();
const showDropdown = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);
const isToggling = ref(false);
const isDeleteClick = ref(false);
const emit = defineEmits<{
  (e: 'onClickPersonnalisation'): void;
}>();
const data = reactive({
  actions: [
    {
      code: '1',
      text: 'Personnaliser',
      icon: 'actions_vertical',
      callback: customize,
    },
  ],
});

onMounted(() => {
  userStore.fetchUserLinks();
  document.addEventListener('click', handleClickOutside);
});

onMounted(() => {
  const icons = document.querySelectorAll('.cs-button-icon[role="img"]');

  icons.forEach(icon => {
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('role', 'presentation');
  });
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

function handleCardClick(event: MouseEvent, url: string) {
  if (isDeleteClick.value == true) {
    isDeleteClick.value = false;
    return;
  }
  openLink(url);
}

function handleDropdown(event?: Event) {
  isToggling.value = true;
  showDropdown.value = !showDropdown.value;

  // Reset the toggle flag after a short delay
  setTimeout(() => {
    isToggling.value = false;
  }, 100);
}
function cancelDelete() {
  linkToDelete.value = null;
  isDeleteClick.value = false;
}
function handleClickOutside(event: MouseEvent) {
  // Don't close if we're currently toggling
  if (isToggling.value) {
    return;
  }

  const target = event.target as Node;

  // Don't close if clicking on the dropdown itself
  if (dropdownRef.value && dropdownRef.value.contains(target)) {
    return;
  }

  // Close dropdown if clicking outside
  showDropdown.value = false;
}

function customize() {
  emit('onClickPersonnalisation');
  showDropdown.value = false;
}
function openLink(url: string) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
const hasNoLinks = computed(() => !userStore.userLinks || userStore.userLinks.length === 0);
const linkToDelete = ref<string | null>(null);

function confirmDelete(linkId: string): void {
  userStore.deleteUserLink(linkId);
  isDeleteClick.value = false;
  linkToDelete.value = null;
}
</script>

<template>
  <cs-widget
    :title="t('dashboard.lienUtilTitle')"
    :class="['my-wrap', { 'no-overflow': hasNoLinks }]"
    :actions="data.actions"
  >
    <cs-confirmation-popup
      class="confirmation-popup-delete"
      v-if="isDeleteClick == true"
      data-qa="confirmation-popup-delete"
      :is-opened="true"
      @confirmation="(result: boolean) => {
    if (result && linkToDelete) {
      confirmDelete(linkToDelete)
    }
    cancelDelete()
  }"
    >
      <template #importantMessage>
        <span v-html="t('modal.confirmDeleteLien')" />
      </template>
    </cs-confirmation-popup>
    <!--<div v-if="showDropdown" class="dropdown-menu" ref="dropdownRef">
      <ul>
        <li @click="customize">{{ t('modal.personnaliser') }}</li>
      </ul>
    </div>-->
    <div v-if="hasNoLinks" class="empty-state" data-qa="empty-state">
      <img :src="undrawLink" :alt="t('links.noLinksImageAlt')" class="empty-state-image" />
      <p class="empty-state-message">{{ t('links.noLinksMessage') }}</p>
      <cs-button
        :text="t('links.addLink')"
        icon="ajouter"
        type="secondary"
        data-qa="add-link-button"
        @click="customize"
      />
    </div>
    <div v-else class="link-grid" data-qa="link-grid">
      <div
        class="link-card"
        v-for="(link, index) in userStore.userLinks"
        :key="link.id"
        @click="handleCardClick($event, link.url)"
        tabindex="0"
        role="link"
        @keydown.enter="openLink(link.url)"
      >
        <div class="cadre-image">
          <img
            v-if="link.imageBase64"
            :src="link.imageBase64"
            data-qa="link-img"
            :alt="link.name"
            class="link-image"
          />
          <div class="icon-wrapper">
            <cs-button
              v-if="link && !link.isAddedByAdmin"
              icon="supprimer"
              type="tertiary"
              size="small"
              status="destructive"
              class="link-icon"
              @click.stop.prevent="
                () => {
                  isDeleteClick = true;
                  linkToDelete = link.id;
                }
              "
              :aria-label="t('links.deleteLink', { name: link.name })"
              :title="t('links.deleteLink', { name: link.name })"
            />
          </div>
        </div>
        <div class="link-label">
          <cs-link
            data-qa="link-url"
            :link="`${link.url}`"
            is-external-link
            target="_blank"
            :aria-label="t('links.openLinkNewWindow', { name: link.name })"
          >
            <b class="titles" data-qa="link-label">{{ link.name }}</b>
            <span class="visually-hidden">(nouvelle fenêtre)</span>
          </cs-link>
        </div>
      </div>
    </div>
  </cs-widget>
</template>

<style scoped>
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem 1rem;
  gap: 1rem;
}

.empty-state-image {
  width: 16rem;
  height: 4.5rem;
}

.empty-state-message {
  font-size: 1rem;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin: 0;
}

.link-grid {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

.link-card {
  width: 13.5rem;
  height: 11rem;
  overflow: hidden;
  transition: transform 0.2s;
  cursor: pointer;
}
.visually-hidden {
  position: absolute !important;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.link-card:hover {
  transform: translateY(-2px);
}

.cadre-image {
  position: relative;
  background-color: #f5f5f5; /* neutral grey */
  width: 13rem;
  height: 7rem;
  border-radius: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  margin-bottom: 0.25rem;
}
.link-icon {
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  font-size: 1.25rem;
  cursor: pointer;
  z-index: 1;
}
.titles {
  color: #333;
}

.link-image {
  width: 6.25rem; /* Adjust as needed */
  height: auto;
  max-height: 100%;
  object-fit: contain;
}

.link-label {
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.25rem;
}
.link-card:hover .titles {
  color: #0167ff;
}

.dropdown-menu {
  position: absolute;
  top: 0rem; /* ajuste verticalement selon ton widget */
  right: 0.75rem; /* positionne à côté du bouton '...' */
  background-color: white;
  border: 1px solid #ccc;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 0.25rem;
  z-index: 100;
}

.dropdown-menu ul {
  list-style: none;
  margin: 0;
  padding: 0.5rem;
}

.dropdown-menu li {
  padding: 0.5rem;
  cursor: pointer;
}

.dropdown-menu li:hover {
  background-color: #f0f0f0;
}
.icon-wrapper {
  position: absolute;
  right: 0rem;
  top: 0rem;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.2s;
}
.link-card:hover .icon-wrapper {
  opacity: 1;
}
:deep(.cs-widget) {
  position: relative !important;
}
.no-overflow :deep(.cs-widget) {
  overflow: hidden;
}
.no-overflow :deep(.cs-widget-content) {
  overflow: hidden;
}
.cs-link {
  gap: 0.25rem;
}
.cs-link:hover:not(.cs-link-disabled) {
  background: none;
}
</style>
